import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  AlertTitle,
} from '@mui/material';
import { Box, CircularProgress, Grid, Link } from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';

interface DialogMergeRequestProps {
  displayTitle: string;
  openDialog: boolean;
  normalContent: React.JSX.Element;
  toggleDialog: () => void;
  inputsValid: () => boolean;
  hasChanges: boolean;
  createMergeRequest: () => Promise<MergeRequestResponse>;
  handleTimeout: () => void;
}

export type MergeRequestResponse = {
  status: 'MR_CREATED' | 'MR_NOT_CREATED' | 'MR_EXISTS' | 'GITLAB_UNREACHABLE';
  url?: string;
  message?: string;
};

export function DialogMergeRequest(props: Readonly<DialogMergeRequestProps>) {
  const {
    openDialog,
    displayTitle,
    toggleDialog,
    inputsValid,
    createMergeRequest,
    hasChanges,
    handleTimeout,
    normalContent,
  } = props;
  const [requestInFlight, setRequestInFlight] = useState(false);
  const [requestResponse, setRequestResponse] = useState<
    MergeRequestResponse | undefined
  >(undefined);

  async function handleSave() {
    if (!inputsValid()) {
      return;
    }
    setRequestInFlight(true);
    try {
      const res = await createMergeRequest();
      setRequestResponse(res);
    } catch (e) {
      setRequestResponse({
        status: 'MR_NOT_CREATED',
        message:
          'There was a network error. The Developer Platform Portal is currently not reachable.',
      });
    }
    setRequestInFlight(false);
  }

  function handleToggleDialog() {
    if (requestInFlight) {
      // we want the user to see our response, hence we don't let them close the dialog
      return;
    }
    toggleDialog();
    setTimeout(() => {
      handleTimeout();
      setRequestInFlight(false);
      setRequestResponse(undefined);
    }, 500);
  }

  const successMessage = (url?: string) => (
    <Alert severity="success">
      <AlertTitle>Success</AlertTitle>
      <span>
        A new&nbsp;
        <Link
          href={url}
          target="_blank"
          rel="noopener"
          underline="always"
          color="inherit"
        >
          <LinkIcon style={{ verticalAlign: 'middle', fontSize: '1.25rem' }} />
          <strong>merge request</strong>
        </Link>
        &nbsp;has been created in our CMDB. We will take the necessary steps to
        follow through with your request.
      </span>
    </Alert>
  );

  const infoMessage = (url?: string) => (
    <Alert severity="info">
      <AlertTitle>Info</AlertTitle>
      <span>
        There already exists a&nbsp;
        <Link
          href={url}
          target="_blank"
          rel="noopener"
          underline="always"
          color="inherit"
        >
          <LinkIcon style={{ verticalAlign: 'middle', fontSize: '1.25rem' }} />
          <strong>merge request</strong>
        </Link>
        &nbsp;with the requested changes.
      </span>
    </Alert>
  );

  const errorMessage = (message?: string) => (
    <Alert severity="error">
      <AlertTitle>Error</AlertTitle>
      {message}
    </Alert>
  );

  function conditionalRender() {
    if (requestInFlight) {
      return (
        <Box sx={{ m: 6 }}>
          <Grid container justifyContent="center">
            <CircularProgress />
          </Grid>
        </Box>
      );
    }

    if (!requestInFlight && requestResponse) {
      const resp = requestResponse;
      switch (resp.status) {
        case 'MR_CREATED': {
          return successMessage(resp.url);
        }
        case 'MR_NOT_CREATED': {
          return errorMessage(resp.message);
        }
        case 'MR_EXISTS': {
          return infoMessage(resp.url);
        }
        case 'GITLAB_UNREACHABLE': {
          return errorMessage(
            'Unfortunately, we cannot reach our Gitlab instance right now. Please try again in a little while.',
          );
        }
        default: {
          return errorMessage(
            'There was an unkown error. We are currently not able to process your request.',
          );
        }
      }
    }
    return normalContent;
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth={'sm'}
      open={openDialog}
      onClose={handleToggleDialog}
    >
      <DialogTitle>{displayTitle}</DialogTitle>
      <DialogContent>{conditionalRender()}</DialogContent>
      <DialogActions>
        {requestResponse ? (
          <Button disabled={requestInFlight} onClick={handleToggleDialog}>
            OK
          </Button>
        ) : (
          <>
            <Button disabled={requestInFlight} onClick={handleToggleDialog}>
              Cancel
            </Button>
            <Button
              disabled={!hasChanges || requestInFlight}
              onClick={handleSave}
            >
              Request
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
